import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Globe, MapPin, Send, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Demande de démonstration",
  "Demande d'information",
  "Être recontacté",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Votre demande a bien été envoyée. Notre équipe vous recontacte sous 24h ouvrées.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Impossible d'envoyer votre demande. Réessayez."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#0A1128]/60"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 nx-fade">
          <div className="text-xs uppercase tracking-[0.2em] text-[#5BC4D1] font-medium mb-4">
            Contact
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight">
            Organisons une{" "}
            <span className="font-semibold">démonstration.</span>
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Vous souhaitez découvrir MeMed Key® et MeMed BV® dans votre
            établissement, échanger avec un expert clinique ou évaluer
            l&apos;impact sur votre programme Antibiotic Stewardship&nbsp;?
            Notre équipe vous répond sous 24 heures ouvrées.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#5BC4D1]/10 border border-[#5BC4D1]/20 text-[#5BC4D1] shrink-0">
                <Mail size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">
                  Email
                </div>
                <div className="text-white mt-0.5">contact@nexodiag.fr</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#5BC4D1]/10 border border-[#5BC4D1]/20 text-[#5BC4D1] shrink-0">
                <Globe size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">
                  Site web
                </div>
                <div className="text-white mt-0.5">www.nexodiag.fr</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#5BC4D1]/10 border border-[#5BC4D1]/20 text-[#5BC4D1] shrink-0">
                <MapPin size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">
                  Implantation
                </div>
                <div className="text-white mt-0.5">
                  France — Société française spécialisée<br />
                  en solutions diagnostiques innovantes
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 p-5 rounded-2xl border border-[#5BC4D1]/20 bg-[#5BC4D1]/5">
            <div className="text-xs uppercase tracking-wider text-[#5BC4D1] font-medium">
              Fiabilité · Réactivité · Performance
            </div>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed">
              Les valeurs qui guident notre accompagnement auprès des
              laboratoires et des établissements de santé.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7 nx-fade">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-[#0E1530] border border-white/10 rounded-3xl p-6 md:p-10 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Nom complet *
                </label>
                <input
                  data-testid="contact-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  placeholder="Dr. Jean Dupont"
                  className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email professionnel *
                </label>
                <input
                  data-testid="contact-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  placeholder="prenom.nom@hopital.fr"
                  className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Téléphone
                </label>
                <input
                  data-testid="contact-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+33 1 23 45 67 89"
                  className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Objet de la demande
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm({ ...form, service: v })}
                >
                  <SelectTrigger
                    data-testid="contact-service"
                    className="w-full bg-[#060B19] border-white/10 rounded-xl px-4 py-6 text-white focus:ring-2 focus:ring-[#5BC4D1]/40"
                  >
                    <SelectValue placeholder="Sélectionnez un objet" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0E1530] border-white/10 text-white">
                    {services.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        data-testid={`service-option-${s}`}
                        className="focus:bg-[#5BC4D1]/10 focus:text-[#5BC4D1]"
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Message *
              </label>
              <textarea
                data-testid="contact-message"
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                placeholder="Décrivez votre établissement, vos volumes estimés, vos enjeux..."
                className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all resize-none"
              />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
              <p className="text-xs text-slate-500 max-w-md">
                En soumettant ce formulaire, vous acceptez que vos informations
                soient utilisées par Nexodiag pour vous recontacter.
              </p>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="group bg-[#5BC4D1] text-[#060B19] font-semibold px-6 py-3 rounded-full hover:bg-[#3DA9B6] transition-colors inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    Envoyer la demande
                    <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
